import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Calendar, User, Clock, ArrowLeft, Share2, Bookmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API } from "@/App";

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error("Error fetching blog post:", error);
        navigate("/blog");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug, navigate]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </Layout>
    );
  }

  if (!post) return null;

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-slate-50 py-16" data-testid="blog-post-hero">
        <div className="container-padding">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </button>
          <div className="max-w-3xl">
            <span className="badge badge-lime mb-4">{post.category}</span>
            <h1 className="heading-1 mb-6">{post.title}</h1>
            <div className="flex flex-wrap items-center gap-6 text-slate-500">
              <span className="flex items-center gap-2">
                <User className="w-5 h-5" />
                {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                {post.read_time}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {new Date(post.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="py-8" data-testid="blog-post-image">
        <div className="container-padding">
          <img
            src={post.image_url}
            alt={post.title}
            className="w-full max-w-4xl mx-auto h-96 object-cover rounded-2xl"
          />
        </div>
      </section>

      {/* Content */}
      <section className="section-padding" data-testid="blog-post-content">
        <div className="container-padding">
          <div className="max-w-3xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                {post.excerpt}
              </p>
              <p className="text-slate-600 leading-relaxed">
                {post.content}
              </p>
              
              {/* Sample content sections */}
              <h2 className="font-heading text-2xl font-medium text-slate-900 mt-12 mb-4">
                Key Takeaways
              </h2>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-lime-500 rounded-full mt-2 flex-shrink-0"></span>
                  Stay updated with the latest industry trends and technologies
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-lime-500 rounded-full mt-2 flex-shrink-0"></span>
                  Continuously invest in your professional development
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-lime-500 rounded-full mt-2 flex-shrink-0"></span>
                  Build meaningful connections in your industry
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-lime-500 rounded-full mt-2 flex-shrink-0"></span>
                  Seek mentorship and be open to feedback
                </li>
              </ul>

              <blockquote className="my-12 pl-6 border-l-4 border-lime-500 italic text-slate-600 text-xl">
                "The best investment you can make is in yourself. The more you learn, the more you'll earn."
                <cite className="block mt-2 text-base not-italic font-medium text-slate-900">— Warren Buffett</cite>
              </blockquote>
            </div>

            {/* Share */}
            <div className="flex items-center justify-between pt-8 mt-8 border-t border-slate-200">
              <div className="flex items-center gap-4">
                <span className="text-sm text-slate-500">Share this article:</span>
                <Button variant="outline" size="sm" className="rounded-full">
                  <Share2 className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" className="rounded-full">
                  <Bookmark className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-slate-900" data-testid="blog-cta">
        <div className="container-padding">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-3xl font-light text-white mb-4">
              Ready to Transform Your Career?
            </h2>
            <p className="text-slate-400 mb-8">
              Explore our courses and get personalized career guidance from our experts.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/courses">
                <Button className="bg-lime-500 text-slate-900 hover:bg-lime-400 rounded-full px-8 py-6">
                  Browse Courses
                </Button>
              </Link>
              <Link to="/counseling">
                <Button variant="outline" className="border-slate-700 text-white hover:bg-slate-800 rounded-full px-8 py-6">
                  Book Counseling
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BlogPostPage;
